import { useRef } from 'react';

import { useProfile } from 'context/profile';
import { useDebouncedWebsocketSubscription } from 'hooks/useDebouncedWebsocketSubscription';
import useInvalidateQueries from 'hooks/useInvalidateQueries';

// Collapse a burst of message signals (a rapid back-and-forth) into one refetch.
const SIGNAL_DEBOUNCE_MS = 300;

// The per-conversation dirty-flag signal — flags only, never content.
type ChatMessageSignal = { chatMessages?: boolean; actorId: string | null };

type Props = {
  conversationId: string;
};

/**
 * Subscribes the currently open conversation's message stream (OLIO-2835) — exactly one, for the
 * selected channel only. MUST be keyed key={conversationId} by the parent: the subscription hook
 * itself re-subscribes when params change, but the handshake refs below (everConnected /
 * skipNextReceived) would carry the OLD channel's state across a facility switch — the new
 * channel's subscribe-time transmit would read as a reconnect and fire one redundant refetch.
 * Remounting resets them per channel. On a signal from someone else it refetches
 * that conversation's messages; own sends already render from the POST response, so suppression
 * prevents a socket echo-duplicate. Renders nothing.
 *
 * Skips the subscribe-time handshake: the channel transmits its connect payload on FIRST connect,
 * which would refetch the messages just fetched. Reuses useWorkflowNudge's pattern — skip only on
 * the first connect and only when it actually carries a transmit. `joinedExisting` means the
 * shared subscription was already live (no transmit follows), so skipping there would swallow the
 * next real signal instead; a reconnect's transmit SHOULD refetch, converging anything missed.
 */
export default function ChannelSubscriber({ conversationId }: Props) {
  const { profile } = useProfile();
  const invalidateQueries = useInvalidateQueries();

  const everConnected = useRef(false);
  const skipNextReceived = useRef(false);

  useDebouncedWebsocketSubscription<ChatMessageSignal>({
    channel: 'ChatConversationChannel',
    // snake_case — cable subscribe frames skip OliveBranch; a bad param makes the server raise
    // and the client's SubscriptionGuarantor retry forever.
    params: { chat_conversation_id: conversationId },
    debounceMs: SIGNAL_DEBOUNCE_MS,
    enabled: !!conversationId,
    onConnected: (meta) => {
      // First connect only — a reconnect's transmit still refetches, converging missed messages.
      if (everConnected.current) return;
      everConnected.current = true;
      if (!meta.joinedExisting) skipNextReceived.current = true;
    },
    onReceived: (messages) => {
      if (skipNextReceived.current) {
        skipNextReceived.current = false;
        return;
      }
      // Batch-aware actor suppression: invalidate only when a signal came from someone else.
      if (messages.every((message) => message.actorId === profile.id)) return;

      invalidateQueries({ chatMessages: { conversationId } });
    },
  });

  return null;
}
